import React, { useState, useEffect } from "react";
import { CgProfile } from "react-icons/cg";
import { CiLogout, CiEdit } from "react-icons/ci";
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import axiosInstance from "../../utils/axios";
import Swal from "sweetalert2";
import { formatDate } from "../../utils/helpers";
import ReactDOM from "react-dom";

const ChangePasswordModal = ({ userData, setShowPasswordModal }) => {
  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChangePassword = async () => {
    if (
      !passwords.oldPassword ||
      !passwords.newPassword ||
      !passwords.confirmPassword
    ) {
      return Swal.fire("Missing Fields", "Please fill all fields", "warning");
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      return Swal.fire(
        "Mismatch",
        "New password and confirm password must match",
        "error"
      );
    }

    try {
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to change your password?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, change it",
        cancelButtonText: "Cancel",
      });

      if (!confirm.isConfirmed) return;

      const resp = await axiosInstance.post(
        `/users/change-password/${userData._id}`,
        {
          oldPassword: passwords.oldPassword,
          newPassword: passwords.newPassword,
        }
      );

      Swal.fire("Success", resp.data.message, "success");
      setShowPasswordModal(false);
      setPasswords({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.error || "Unable to change password",
        "error"
      );
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Change Password
        </h2>

        <div className="space-y-3">
          <input
            type="password"
            placeholder="Old Password"
            value={passwords.oldPassword}
            onChange={(e) =>
              setPasswords({ ...passwords, oldPassword: e.target.value })
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F]"
          />
          <input
            type="password"
            placeholder="New Password"
            value={passwords.newPassword}
            onChange={(e) =>
              setPasswords({ ...passwords, newPassword: e.target.value })
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F]"
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={passwords.confirmPassword}
            onChange={(e) =>
              setPasswords({ ...passwords, confirmPassword: e.target.value })
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F]"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setShowPasswordModal(false)}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleChangePassword}
            className="px-4 py-2 bg-[#02323F] hover:bg-[#045266] text-white rounded-lg"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Profile = () => {
  const [userData, setUserData] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [totalDonations, setTotalDonations] = useState(0);
  const [recentDonations, setRecentDonations] = useState([]);
  const [editData, setEditData] = useState({});
  const [visibleCount, setVisibleCount] = useState(5);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const getTotalDonations = async () => {
    try {
      const response = await axiosInstance.get(
        `/donations/total/${userData?._id}`
      );
      setTotalDonations(response.data?.totalDonations);
    } catch (error) {
      console.error("Error fetching totalDonations:", error);
    } finally {
      setLoadingStats(false);
    }
  };
  const getRecentActivity = async () => {
    try {
      const response = await axiosInstance.get(
        `/donations/recent/${userData?._id}`
      );
      setRecentDonations(response.data);
    } catch (error) {
      console.error("Error fetching recent donations:", error);
    } finally {
      setLoadingActivity(false);
    }
  };

  useEffect(() => {
    if (userData?._id) {
      getTotalDonations();
      getRecentActivity();
    }
  }, [userData._id]);

  useEffect(() => {
    const token = localStorage.getItem("ngo-afsa-token");
    const user = JSON.parse(localStorage.getItem("ngo-afsa-data") || "{}");

    if (token) {
      setIsLoggedIn(true);
      const mockUserData = {
        name: user.name || "John Doe",
        email: user.email || "john.doe@example.com",
        phoneNumber: user.phoneNumber || "+91 9999999999",
        joinDate: "January 2024",
        address: "",
        isVerified: true,
        ...user,
      };
      setUserData(mockUserData);
      setEditData(mockUserData);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("ngo-afsa-token");
    localStorage.removeItem("ngo-afsa-data");
    setIsLoggedIn(false);
    window.location.href = "/";
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError("");
      const payload = {
        name: editData.name,
        email: editData.email,
        phoneNumber: editData.phoneNumber,
        address: editData.address,
      };
      // console.log("edit data:", payload);
      const resp = await axiosInstance.post(
        `/users/update-by-user-id/${userData?._id}`,
        payload
      );
      // console.log("resp", resp);
      setUserData(resp.data?.user);
      localStorage.setItem("ngo-afsa-data", JSON.stringify(resp.data?.user));
      Swal.fire({
        title: "Profile Updated !",
        text: "Your changes were saved successfully.",
        icon: "success",
        confirmButtonText: "Great!",
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      console.log("Error updating user data,", error);
      setError(error?.message || "Couldn't update");
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditData(userData);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#02323F] via-[#0a4a5c] to-[#02323F] flex items-center justify-center px-4">
        <div className="text-center">
          <CgProfile className="text-8xl text-white/20 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-8">
            Please log in to view your profile
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="bg-[#FCC101] hover:bg-[#e6b800] text-white font-semibold px-8 py-3 rounded-xl transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header Section */}
      {showPasswordModal && (
        <ChangePasswordModal
          userData={userData}
          setShowPasswordModal={setShowPasswordModal}
        />
      )}
      <div className="bg-gradient-to-r from-[#02323F] to-[#0a4a5c] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Profile Avatar */}
            <div className="relative">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border-4 border-white/20">
                <CgProfile className="text-5xl sm:text-6xl text-white" />
              </div>
              {userData.isVerified && (
                <MdVerified className="absolute -bottom-1 -right-1 text-2xl text-green-400 bg-white rounded-full p-1" />
              )}
            </div>

            {/* Profile Info */}
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">
                {userData.name}
              </h1>
              <p className="text-white/80 text-lg mb-4">{userData.email}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 bg-[#FCC101] hover:bg-[#e6b800] text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 hover:scale-[101%]"
                >
                  <CiEdit className="text-xl" />
                  Edit Profile
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-3 rounded-xl transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-4 py-3 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 hover:scale-[101%]"
              >
                <CiLogout className="text-xl" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Personal Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8 hover:shadow-2xl transition-shadow duration-300">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <FaUser className="text-[#02323F]" />
                Personal Information
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                    <FaUser className="text-xs" />
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.name || ""}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                    />
                  ) : (
                    <p className="text-lg font-semibold text-gray-800 bg-gray-50 p-3 rounded-xl">
                      {userData.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                    <FaEnvelope className="text-xs" />
                    Email Address
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editData.email || ""}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                    />
                  ) : (
                    <p className="text-lg font-medium text-gray-700 bg-gray-50 p-3 rounded-xl break-words">
                      {userData.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                    <FaPhone className="text-xs" />
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editData.phoneNumber || ""}
                      onChange={(e) =>
                        handleInputChange("phoneNumber", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                    />
                  ) : (
                    <p className="text-lg font-medium text-gray-700 bg-gray-50 p-3 rounded-xl">
                      {userData.phoneNumber}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                    <FaMapMarkerAlt className="text-xs" />
                    Address
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.address || ""}
                      onChange={(e) =>
                        handleInputChange("address", e.target.value)
                      }
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                    />
                  ) : (
                    <p className="text-lg font-medium text-gray-700 bg-gray-50 p-3 rounded-xl">
                      {userData.address}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-shadow duration-300">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Recent Activity
              </h2>

              <div className="space-y-4 flex flex-col">
                {loadingActivity ? (
                  Array.from({ length: 3 }).map((_, idx) => (
                    <div
                      key={idx}
                      className="h-16 bg-gray-200 rounded-xl animate-pulse"
                    ></div>
                  ))
                ) : recentDonations.length > 0 ? (
                  <>
                    {recentDonations.slice(0, visibleCount).map((donation) => (
                      <div
                        key={donation._id}
                        className="flex items-center gap-4 p-4 bg-green-50 rounded-xl border-l-4 border-green-500"
                      >
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            Donated ₹{donation?.amount}
                          </p>
                          <p className="text-sm text-gray-600">
                            {donation.name && (
                              <>
                                <span>Towards Program - {donation?.name}</span>
                                <span className="px-1">•</span>
                              </>
                            )}
                            {formatDate(donation?.date)}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* View More Button */}
                    {visibleCount < recentDonations.length ? (
                      <button
                        onClick={() => setVisibleCount((prev) => prev + 5)}
                        className="mt-4 px-4 py-2 bg-[#02323F] text-white rounded-xl hover:bg-[#045266] transition-all w-fit self-center"
                      >
                        View More
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          setVisibleCount((prev) => {
                            if (prev > 5) {
                              return prev - 5;
                            }
                          })
                        }
                        className="mt-4 px-4 py-2 bg-[#02323F] text-white rounded-xl hover:bg-[#045266] transition-all w-fit self-center"
                      >
                        View Less
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500">No recent donations found.</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 ">
            {/* Quick Stats */}
            {loadingStats ? (
              <>
                <div className="h-12 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="h-12 bg-gray-200 rounded-lg animate-pulse"></div>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-700">Total Donations</span>
                  <span className="font-bold text-green-600">
                    {totalDonations || 0}
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-gray-700">Member Since</span>
                  <span className="font-bold text-purple-600">
                    {formatDate(userData?.createdAt)}
                  </span>
                </div>
              </>
            )}

            {/* Account Settings */}
            <div className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow duration-300">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Account Settings
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 hover:text-[#02323F]"
                >
                  Change Password
                </button>
                {/* <button className="w-full text-left p-3 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700">
                  Delete Account
                </button> */}
              </div>  
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
